/**
 * Tienda de Complementos — página pública (autenticada) con precios dinámicos.
 * Muestra cada add-on con descripción, cómo funciona, beneficios, precio.
 * Botón "Probar 7 días gratis" → dispara /api/complementos/{feature_key}/activar.
 * Muestra el cargo prorrateado estimado ANTES de confirmar.
 */
import { useState, useEffect } from 'react';
import api, { formatoMXN } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Sparkles, Check, Clock, Lock, ShoppingBag, Info } from 'lucide-react';

export default function Complementos() {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState(null); // {complemento, cargo_prorrateado, ...}
  const [confirmar, setConfirmar] = useState(null); // complemento a activar
  const [activando, setActivando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await api.get('/complementos/publicos');
      setItems(r.data.complementos || []);
    } catch (e) {
      toast.error('Error cargando complementos');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const activar = async () => {
    if (!confirmar) return;
    setActivando(true);
    try {
      const r = await api.post(`/complementos/${confirmar.feature_key}/activar`);
      toast.success(r.data.mensaje, { duration: 6000 });
      setDetalle(r.data);
      setConfirmar(null);
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al activar');
    } finally {
      setActivando(false);
    }
  };

  const cancelar = async (fk, nombre) => {
    if (!window.confirm(`¿Cancelar "${nombre}"? Perderás acceso al final del ciclo.`)) return;
    try {
      await api.post(`/complementos/${fk}/cancelar`);
      toast.success('Complemento cancelado');
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="space-y-6" data-testid="complementos-page">
      <div className="flex items-center gap-3">
        <ShoppingBag className="w-8 h-8 text-amber-400" />
        <div>
          <h1 className="text-3xl font-bold text-slate-100 font-barlow">Tienda de Complementos</h1>
          <p className="text-sm text-slate-400">Amplía tu sistema con herramientas premium. Prueba 7 días gratis cualquier complemento.</p>
        </div>
      </div>

      {cargando ? (
        <div className="grid md:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-96 bg-slate-800/40 rounded animate-pulse" />)}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map(c => {
            const yaActivo = c.activo_como_addon || c.incluido_en_plan;
            const enTrial = c.en_trial;
            return (
              <Card key={c.feature_key}
                className={`bg-slate-900/60 border transition ${
                  yaActivo ? 'border-emerald-500/40 shadow-[0_0_25px_rgba(16,185,129,0.15)]'
                           : 'border-slate-800 hover:border-amber-500/40'
                }`}
                data-testid={`complemento-card-${c.feature_key}`}>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-slate-100 font-barlow leading-tight">{c.nombre}</h3>
                      <p className="text-xs text-slate-400 mt-1">{c.descripcion_corta}</p>
                    </div>
                    {yaActivo && !c.incluido_en_plan && (
                      <span className="text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2 py-1 rounded" data-testid={`badge-activo-${c.feature_key}`}>
                        {enTrial ? `Trial ${c.trial_dias_restantes}d` : 'Activo'}
                      </span>
                    )}
                    {c.incluido_en_plan && (
                      <span className="text-[10px] uppercase tracking-wider bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 px-2 py-1 rounded">
                        En tu plan
                      </span>
                    )}
                  </div>

                  <div className="pb-3 border-b border-slate-800">
                    <div className="flex items-baseline gap-1" data-testid={`precio-${c.feature_key}`}>
                      <span className="text-3xl font-bold text-amber-400 font-mono">${c.precio_mensual.toFixed(0)}</span>
                      <span className="text-xs text-slate-500">{c.moneda}/mes</span>
                    </div>
                    <p className="text-[11px] text-emerald-400 mt-1 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> 7 días de prueba gratis
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Cómo funciona</p>
                    <ol className="space-y-1.5">
                      {(c.como_funciona || []).slice(0, 4).map((paso, i) => (
                        <li key={i} className="flex gap-2 text-xs text-slate-300 leading-relaxed">
                          <span className="text-amber-500 font-bold shrink-0">{i + 1}.</span>
                          <span>{paso}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Beneficios</p>
                    <ul className="space-y-1">
                      {(c.beneficios || []).slice(0, 4).map((b, i) => (
                        <li key={i} className="flex gap-2 text-xs text-slate-300">
                          <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {c.incluido_en_plan ? (
                    <div className="w-full flex items-center justify-center gap-2 py-2.5 text-cyan-400 bg-cyan-500/10 rounded text-sm">
                      <Check className="w-4 h-4" /> Ya lo tienes en tu plan
                    </div>
                  ) : yaActivo ? (
                    <div className="space-y-2">
                      {enTrial && (
                        <div className="text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded px-3 py-2 flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5" />
                          <span>Trial activo · {c.trial_dias_restantes} día{c.trial_dias_restantes !== 1 ? 's' : ''} restante{c.trial_dias_restantes !== 1 ? 's' : ''}</span>
                        </div>
                      )}
                      <Button onClick={() => cancelar(c.feature_key, c.nombre)}
                        variant="outline" size="sm"
                        className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                        data-testid={`cancelar-${c.feature_key}`}>
                        Cancelar complemento
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={() => setConfirmar(c)}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold"
                      data-testid={`activar-${c.feature_key}`}>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Probar 7 días gratis
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialogo de confirmación con desglose de cobro */}
      <Dialog open={!!confirmar} onOpenChange={(v) => !v && setConfirmar(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg" data-testid="confirmar-activacion-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl text-slate-100 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" />
              Activar {confirmar?.nombre}
            </DialogTitle>
          </DialogHeader>
          {confirmar && (
            <div className="space-y-4">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-4">
                <p className="text-sm font-semibold text-emerald-300 mb-2">
                  🎁 Tus primeros 7 días son totalmente GRATIS
                </p>
                <p className="text-xs text-emerald-200/80">
                  Disfruta {confirmar.nombre} sin costo desde hoy. Puedes cancelar cuando quieras dentro de la prueba y no se cobra nada.
                </p>
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded p-4 space-y-2 text-sm">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Después del trial</p>
                <div className="flex justify-between text-slate-300">
                  <span>Precio mensual regular:</span>
                  <span className="font-mono font-bold text-amber-400">{formatoMXN(confirmar.precio_mensual)}</span>
                </div>
                <div className="text-xs text-slate-500 leading-relaxed pt-2 border-t border-slate-800">
                  Al terminar los 7 días de prueba, se cobrará <strong className="text-amber-400">solo por los días usados</strong> del ciclo actual (prorrateado) más el mes completo en tu próxima renovación. Total sin sorpresas.
                </div>
              </div>

              <div className="bg-slate-800/40 border border-slate-800 rounded p-3 flex gap-2">
                <Info className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-400">
                  Puedes cancelar en cualquier momento desde esta misma página. Sin permanencia forzada.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmar(null)} className="text-slate-400" data-testid="cancelar-activacion-btn">
              Cancelar
            </Button>
            <Button onClick={activar} disabled={activando}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
              data-testid="confirmar-activacion-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              {activando ? 'Activando...' : 'Empezar 7 días gratis'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo con resumen post-activación */}
      <Dialog open={!!detalle} onOpenChange={(v) => !v && setDetalle(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="detalle-activacion-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl text-emerald-400 flex items-center gap-2">
              <Check className="w-5 h-5" /> ¡Activado!
            </DialogTitle>
          </DialogHeader>
          {detalle && (
            <div className="space-y-3">
              <p className="text-sm text-slate-300">{detalle.mensaje}</p>
              <div className="bg-slate-950 border border-slate-800 rounded p-3 text-xs space-y-1">
                <p className="text-slate-400"><strong className="text-amber-400">Resumen del cobro:</strong></p>
                <p className="text-slate-300 leading-relaxed">{detalle.resumen_cobro}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setDetalle(null)} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
